/**
 * Uma conta, uma câmera. A reconexão às vezes entra com um peer novo antes
 * do antigo sair, e a lista da API só guarda um registro por pessoa — por
 * isso o servidor não enxerga a cópia. Quem está na sala vê as duas.
 * Só o dono da sala expulsa, e só a conexão antiga.
 */

export type SeenPeer = { id: string; seenAt: number };

/**
 * Devolve os ids que devem sair. `pinId` é a conexão atual de quem está
 * decidindo: as outras da mesma conta são fantasmas, mesmo que tenham
 * aparecido juntas. Sem pin, só expulsa quando uma conexão é claramente
 * mais nova. Empate não chuta ninguém.
 */
export function idsToKick(peers: SeenPeer[], pinId?: string): string[] {
  const live = peers.filter((peer) => peer.id);
  if (live.length < 2) return [];
  if (pinId && live.some((peer) => peer.id === pinId)) {
    return live.filter((peer) => peer.id !== pinId).map((peer) => peer.id);
  }
  const newest = Math.max(...live.map((peer) => peer.seenAt));
  const keepers = live.filter((peer) => peer.seenAt === newest);
  if (keepers.length !== 1) return [];
  return live.filter((peer) => peer.id !== keepers[0].id).map((peer) => peer.id);
}

type KickablePeer = {
  id: string;
  customParticipantId?: string;
  kick?: () => Promise<void> | void;
};

type DuplicateMeeting = {
  self: { id?: string; customParticipantId?: string };
  participants: {
    joined: {
      toArray: () => KickablePeer[];
      on: (event: "participantJoined" | "participantLeft", handler: () => void) => void;
      off?: (event: "participantJoined" | "participantLeft", handler: () => void) => void;
      removeListener?: (event: "participantJoined" | "participantLeft", handler: () => void) => void;
    };
  };
};

export function watchDuplicatePeers(meeting: DuplicateMeeting): () => void {
  const seenAt = new Map<string, number>();
  const kicking = new Set<string>();
  let timer: number | undefined;

  const sweep = () => {
    timer = undefined;
    const now = Date.now();
    const selfId = meeting.self?.id || "";
    const selfUid = meeting.self?.customParticipantId || "";
    const groups = new Map<string, { peers: SeenPeer[]; byId: Map<string, KickablePeer> }>([]);

    const remember = (id: string) => {
      if (!seenAt.has(id)) seenAt.set(id, now);
      return seenAt.get(id) as number;
    };

    if (selfId && selfUid) {
      groups.set(selfUid, {
        peers: [{ id: selfId, seenAt: remember(selfId) }],
        byId: new Map(),
      });
    }

    for (const peer of meeting.participants.joined.toArray()) {
      const uid = peer.customParticipantId || "";
      if (!uid || !peer.id || peer.id === selfId) continue;
      let group = groups.get(uid);
      if (!group) {
        group = { peers: [], byId: new Map<string, KickablePeer>() };
        groups.set(uid, group);
      }
      group.peers.push({ id: peer.id, seenAt: remember(peer.id) });
      group.byId.set(peer.id, peer);
    }

    for (const [uid, group] of Array.from(groups.entries())) {
      const pin = uid === selfUid ? selfId : undefined;
      for (const id of idsToKick(group.peers, pin)) {
        const peer = group.byId.get(id);
        if (!peer?.kick || kicking.has(id)) continue;
        kicking.add(id);
        Promise.resolve(peer.kick()).catch(() => {
          kicking.delete(id);
        });
      }
    }
  };

  const schedule = () => {
    if (timer !== undefined) return;
    timer = window.setTimeout(sweep, 500);
  };

  const joined = meeting.participants.joined;
  joined.on("participantJoined", schedule);
  joined.on("participantLeft", schedule);
  schedule();

  return () => {
    joined.off?.("participantJoined", schedule);
    joined.off?.("participantLeft", schedule);
    joined.removeListener?.("participantJoined", schedule);
    joined.removeListener?.("participantLeft", schedule);
    if (timer !== undefined) window.clearTimeout(timer);
  };
}
